// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/AdvisorLauncher.jsx

import React, { useEffect, useState, useCallback } from "react";
import AdvisorDock from "./AdvisorDock";

/**
 * AdvisorLauncher
 *
 * Global launcher for OASYS Copilot.
 *
 * The launcher is intentionally global rather than page-embedded:
 * - one Copilot access point across OASYS
 * - fixed viewport position while users scroll
 * - page-aware context is still supplied by AdvisorContext
 * - the full dock opens as a floating intelligence panel
 */
const LAUNCHER_POS_KEY = "oasys.copilotLauncher.pos.v2";
const OPEN_COPILOT_EVENT = "oasys:open-copilot";
const TOGGLE_COPILOT_EVENT = "oasys:toggle-copilot";

function clampNumber(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function getViewport() {
  if (typeof window === "undefined") {
    return { width: 1200, height: 800 };
  }

  return {
    width: window.innerWidth || 1200,
    height: window.innerHeight || 800,
  };
}

function clampLauncherPosition(right, bottom) {
  const viewport = getViewport();

  return {
    right: clampNumber(Number(right) || 24, 8, Math.max(8, viewport.width - 112)),
    bottom: clampNumber(Number(bottom) || 24, 8, Math.max(8, viewport.height - 64)),
  };
}

function readLauncherPosition(fallbackRight, fallbackBottom) {
  if (typeof window === "undefined") {
    return {
      right: fallbackRight,
      bottom: fallbackBottom,
    };
  }

  try {
    const raw = window.localStorage.getItem(LAUNCHER_POS_KEY);
    if (!raw) {
      return clampLauncherPosition(fallbackRight, fallbackBottom);
    }

    const parsed = JSON.parse(raw);
    if (typeof parsed?.right === "number" && typeof parsed?.bottom === "number") {
      return clampLauncherPosition(parsed.right, parsed.bottom);
    }
  } catch {
    // Ignore corrupted persisted position.
  }

  return clampLauncherPosition(fallbackRight, fallbackBottom);
}

function saveLauncherPosition(position) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(LAUNCHER_POS_KEY, JSON.stringify(position));
  } catch {
    // Ignore storage failures.
  }
}

export default function AdvisorLauncher({
  mode = "dashboard",
  themeMode = "light",
  rightOffset = 24,
  bottomOffset = 24,
  draggable = true,
  defaultOpen = false,
  sidebarLauncher = false,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [chipPos, setChipPos] = useState(() =>
    readLauncherPosition(rightOffset, bottomOffset)
  );

  useEffect(() => {
    const next = readLauncherPosition(rightOffset, bottomOffset);
    setChipPos(next);
  }, [rightOffset, bottomOffset]);

  useEffect(() => {
    const onResize = () => {
      setChipPos((current) => {
        const next = clampLauncherPosition(current.right, current.bottom);
        saveLauncherPosition(next);
        return next;
      });
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const openCopilot = () => setOpen(true);

    const toggleCopilot = () => setOpen((current) => !current);
    window.addEventListener(OPEN_COPILOT_EVENT, openCopilot);
    window.addEventListener(TOGGLE_COPILOT_EVENT, toggleCopilot);
    return () => {
      window.removeEventListener(OPEN_COPILOT_EVENT, openCopilot);
      window.removeEventListener(TOGGLE_COPILOT_EVENT, toggleCopilot);
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const updateChipPos = useCallback((nextPosition) => {
    const next = clampLauncherPosition(nextPosition.right, nextPosition.bottom);
    setChipPos(next);
    saveLauncherPosition(next);
  }, []);

  const handleDockPositionChange = useCallback(
    ({ right, bottom }) => {
      updateChipPos({
        right,
        bottom: Math.max(16, bottom - 62),
      });
    },
    [updateChipPos]
  );

  const dockId = "advisor-dock";
  const dockRight = chipPos.right;
  const dockBottom = chipPos.bottom + 64;

  return (
    <>
      {!sidebarLauncher && <button
        type="button"
        className="advisor-launcher"
        data-open={open ? "true" : "false"}
        onClick={() => setOpen((value) => !value)}
        title={open ? "Close OASYS Copilot" : "Open OASYS Copilot"}
        aria-label={open ? "Close OASYS Copilot" : "Open OASYS Copilot"}
        aria-expanded={open}
        aria-controls={dockId}
        style={{
          position: "fixed",
          right: chipPos.right,
          bottom: chipPos.bottom,
          zIndex: 72,
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 16px",
          minHeight: 48,
          borderRadius: 999,
          border:
            themeMode === "dark"
              ? "1px solid rgba(255,255,255,.16)"
              : "1px solid rgba(12,21,51,.12)",
          background:
            themeMode === "dark"
              ? "linear-gradient(180deg, rgba(20, 30, 54, .96), rgba(14, 21, 39, .96))"
              : "linear-gradient(180deg, #ffffff, #f6f8fc)",
          color: themeMode === "dark" ? "#eaf0ff" : "#0c1533",
          boxShadow:
            themeMode === "dark"
              ? "0 16px 34px rgba(0,0,0,.42)"
              : "0 16px 30px rgba(10,36,99,.18)",
          cursor: "pointer",
          userSelect: "none",
          backdropFilter: "blur(8px)",
        }}
      >
        <span
          className="advisor-launcher-avatar"
          aria-hidden="true"
          style={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            fontWeight: 900,
            fontSize: open ? 18 : 11,
            lineHeight: 1,
            color: "#0b1220",
            backgroundColor: "#7dd3fc",
            boxShadow: "inset 0 0 0 2px rgba(0,0,0,.12)",
          }}
        >
          {open ? "×" : "OC"}
        </span>

        <span
          className="advisor-launcher-label"
          style={{
            fontWeight: 800,
            fontSize: 13,
            opacity: 0.96,
            whiteSpace: "nowrap",
          }}
        >
          OASYS Copilot
        </span>
      </button>}

      {open && (
        <AdvisorDock
          placement="floating"
          draggable={draggable}
          mode={mode}
          themeMode={themeMode}
          rightOffset={dockRight}
          bottomOffset={dockBottom}
          onPositionChange={handleDockPositionChange}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
